import {MouseEventHandler, useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE,} from "../../../api/types/commercialCategory";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import SearchFilters from "../../../common/components/SearchFilters";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {fromGenericBeToGenericFe} from "../../../utils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const UpsellingRateFilters = ({
                                  handleClickResetSearch,
                                  handleGetUpsellingRateList,
                                  testFilters,
                                  setTestFilters
                              }: {
    handleGetUpsellingRateList: MouseEventHandler<HTMLButtonElement>;
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>;
    testFilters: any
    setTestFilters: any
}) => {
    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use State
    const [legalEntityError, setLegalEntityError] = useState<string>("");
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);
    const currentSegmentRef = useRef<string | null>(null);

    // Use Repository Hooks
    const {getCommercialCategories, getCommercialCategoriesByCommercialSegmentId} = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    // Use Context
    const {
        globalData: {user}
    } = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    useEffect(() => {
        if (!commercialSegmentList?.length) {
            awaitCommercialSegments();
        }
        // eslint-disable-next-line
    }, []);

    // Use Effect
    useEffect(() => {
        const segmentCode = testFilters.commercialSegmentId
            ? (testFilters.commercialSegmentId as SelectElementModelFE).code as string
            : null;
        
        currentSegmentRef.current = segmentCode;

        const getContent = async () => {
            // Use the first selected legal entity as active tenant for localization, fallback to first available
            const selectedLegalEntities = testFilters.legalEntities as SelectElementModelFE[];
            const activeTenantId = selectedLegalEntities?.length > 0 
                ? selectedLegalEntities[0].code 
                : user.legalEntities?.[0]?.name;
            
            let commercialCategoriesData;
            if (segmentCode) {
                commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId(segmentCode);
            } else {
                commercialCategoriesData = await getCommercialCategories();
            }

            if (currentSegmentRef.current !== segmentCode) {
                return;
            }

            const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData, activeTenantId);
            setCommercialCategoryListOriginal(commercialCategoryList);
        };
        getContent();
        // eslint-disable-next-line
    }, [testFilters.commercialSegmentId]);

    // Effect per gestire il filtraggio client-side delle commercial categories quando cambiano le legal entities
    useEffect(() => {
        if (testFilters.commercialSegmentId) {
            // Se c'è un commercial segment selezionato, mantieni il filtraggio per segment
            // ma applica anche il filtraggio e l'espansione per legal entities
            if (testFilters.legalEntities && Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length > 0) {
                const selectedLegalEntityCodes = testFilters.legalEntities.map((le: SelectElementModelFE) => le.code);
                
                const filteredCategories = commercialCategoryListOriginal.filter(category => 
                    category.labels && selectedLegalEntityCodes.some((leCode: string | number) => 
                        category.labels?.some(label => label.country === leCode)
                    )
                );
                
                // "Esplodi" le categorie: crea una voce separata per ogni label unico
                const expandedCategories: CommercialCategorySelectFE[] = [];
                
                filteredCategories.forEach(category => {
                    if (category.labels && category.labels.length > 0) {
                        const relevantLabels = category.labels.filter(label => 
                            selectedLegalEntityCodes.includes(label.country)
                        );
                        
                        const uniqueLabelsMap = new Map<string, typeof relevantLabels>();
                        relevantLabels.forEach(labelObj => {
                            if (!uniqueLabelsMap.has(labelObj.label)) {
                                uniqueLabelsMap.set(labelObj.label, [labelObj]);
                            } else {
                                uniqueLabelsMap.get(labelObj.label)!.push(labelObj);
                            }
                        });
                        
                        uniqueLabelsMap.forEach((labelGroup, uniqueLabel) => {
                            expandedCategories.push({
                                ...category,
                                label: uniqueLabel,
                                labels: labelGroup
                            });
                        });
                    } else {
                        expandedCategories.push(category);
                    }
                });
                
                setCommercialCategoryList(expandedCategories);
            } else {
                // Espandi tutte le label per tutte le legal entities
                const expandedCategories: CommercialCategorySelectFE[] = [];
                commercialCategoryListOriginal.forEach(category => {
                    if (category.labels && category.labels.length > 0) {
                        const uniqueLabelsMap = new Map<string, typeof category.labels>();
                        category.labels.forEach(labelObj => {
                            if (!uniqueLabelsMap.has(labelObj.label)) {
                                uniqueLabelsMap.set(labelObj.label, [labelObj]);
                            } else {
                                uniqueLabelsMap.get(labelObj.label)!.push(labelObj);
                            }
                        });
                        uniqueLabelsMap.forEach((labelGroup, uniqueLabel) => {
                            expandedCategories.push({
                                ...category,
                                label: uniqueLabel,
                                labels: labelGroup
                            });
                        });
                    } else {
                        expandedCategories.push(category);
                    }
                });
                setCommercialCategoryList(expandedCategories);
            }
        } else {
            // Filtra e "esplodi" le categorie in base alle legal entities selezionate
            if (testFilters.legalEntities && Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length > 0) {
                const selectedLegalEntityCodes = testFilters.legalEntities.map((le: SelectElementModelFE) => le.code);
                
                const filteredCategories = commercialCategoryListOriginal.filter(category => 
                    category.labels && selectedLegalEntityCodes.some((leCode: string | number) => 
                        category.labels?.some(label => label.country === leCode)
                    )
                );
                
                // "Esplodi" le categorie: crea una voce separata per ogni label unico
                const expandedCategories: CommercialCategorySelectFE[] = [];
                
                filteredCategories.forEach(category => {
                    if (category.labels && category.labels.length > 0) {
                        const relevantLabels = category.labels.filter(label => 
                            selectedLegalEntityCodes.includes(label.country)
                        );
                        
                        const uniqueLabelsMap = new Map<string, typeof relevantLabels>();
                        relevantLabels.forEach(labelObj => {
                            if (!uniqueLabelsMap.has(labelObj.label)) {
                                uniqueLabelsMap.set(labelObj.label, [labelObj]);
                            } else {
                                uniqueLabelsMap.get(labelObj.label)!.push(labelObj);
                            }
                        });
                        
                        uniqueLabelsMap.forEach((labelGroup, uniqueLabel) => {
                            expandedCategories.push({
                                ...category,
                                label: uniqueLabel,
                                labels: labelGroup
                            });
                        });
                    } else {
                        expandedCategories.push(category);
                    }
                });
                
                setCommercialCategoryList(expandedCategories);
            } else {
                // Espandi tutte le label per tutte le legal entities
                const allExpandedCategories: CommercialCategorySelectFE[] = [];
                commercialCategoryListOriginal.forEach(category => {
                    if (category.labels && category.labels.length > 0) {
                        const uniqueLabelsMap = new Map<string, typeof category.labels>();
                        category.labels.forEach(labelObj => {
                            if (!uniqueLabelsMap.has(labelObj.label)) {
                                uniqueLabelsMap.set(labelObj.label, [labelObj]);
                            } else {
                                uniqueLabelsMap.get(labelObj.label)!.push(labelObj);
                            }
                        });
                        uniqueLabelsMap.forEach((labelGroup, uniqueLabel) => {
                            allExpandedCategories.push({
                                ...category,
                                label: uniqueLabel,
                                labels: labelGroup
                            });
                        });
                    } else {
                        allExpandedCategories.push(category);
                    }
                });
                setCommercialCategoryList(allExpandedCategories);
            }
        }
    }, [testFilters.legalEntities, commercialCategoryListOriginal]);

    // Validation function for mandatory legal entity
    const validateLegalEntity = (): boolean => {
        if (!testFilters.legalEntities || !Array.isArray(testFilters.legalEntities) || testFilters.legalEntities.length === 0) {
            setLegalEntityError(t("pricing:validation.legalEntityRequired", "Select at least one option"));
            return false;
        }
        setLegalEntityError("");
        return true;
    };

    // Effect per validare legal entities quando cambiano
    useEffect(() => {
        if (!testFilters.legalEntities || !Array.isArray(testFilters.legalEntities) || testFilters.legalEntities.length === 0) {
            setLegalEntityError(t("pricing:validation.legalEntityRequired", "Select at least one option"));
        } else {
            setLegalEntityError("");
        }
    }, [testFilters.legalEntities]);

    // Enhanced search handler with validation
    const handleValidatedSearch: MouseEventHandler<HTMLButtonElement> = (e) => {
        if (!validateLegalEntity()) {
            return;
        }
        handleGetUpsellingRateList(e);
    };

    const awaitCommercialSegments = async () => {
        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] = fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "name",
            value: testFilters.name as string,
            label: `${t("pricing:upsellingRate.filter.labels.name")}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, name: value}),
            options: [],
            type: "TextField",
        },
        {
            name: "commercialSegment",
            value: testFilters.commercialSegmentId as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null,
                }),
            options: commercialSegmentList,
            type: "Autocomplete",
        },
        {
            name: "commercialCategory",
            value: testFilters.commercialCategoryId as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.commercialCategory")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value,
                    commercialSegmentId: null,
                }),
            options: commercialCategoryList,
            type: "Autocomplete",
            disabled: !testFilters.legalEntities || !Array.isArray(testFilters.legalEntities) || testFilters.legalEntities.length === 0
        },
    ];
    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "from",
            value: testFilters.from as string,
            options: [],
            label: `${t("pricing:upsellingRate.filter.labels.startDateValidity")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    from: value,
                }),
            type: "Date",
        },
        {
            name: "to",
            value: testFilters.to as string,
            options: [],
            label: `${t("pricing:upsellingRate.filter.labels.endDateValidity")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    to: value,
                }),
            type: "Date",
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as any, // Cast per supportare array
            label: `${t("pricing:upsellingRate.filter.labels.legalEntity")}*`,
            handleChange: (value: SelectElementModelFE[]) =>
                setTestFilters({
                    ...testFilters, 
                    legalEntities: value || [],
                    commercialCategoryId: null // Reset dependent fields
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            error: !!legalEntityError,
            helperText: legalEntityError
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    return (
        // <CardContainer>
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleValidatedSearch}
            handleClickResetSearch={handleClickResetSearch}
        />
        // </CardContainer>
    );
};

export default UpsellingRateFilters;
